import type { PositionBucket } from '../api/types';
import { count } from '../utils/format';

import { POSITION_COLOR, POSITION_LABEL, POSITION_ORDER } from './palette';

/**
 * Composition of one whole across four ordered states. A single stacked bar
 * reads that in one pass; a pie would make the reader compare angles, and four
 * separate bars would lose the "these add up to the catalogue" fact. Segments
 * carry a 2px surface gap and every one is directly labelled below.
 */
export function PositionComposition({ buckets }: { buckets: PositionBucket[] }) {
  const byPosition = new Map(buckets.map((bucket) => [bucket.position, Number(bucket.count)]));
  const total = buckets.reduce((sum, bucket) => sum + Number(bucket.count), 0);

  if (total === 0) {
    return <div className="state">Nothing scanned yet.</div>;
  }

  return (
    <div>
      <div className="compbar" role="img" aria-label="Share of products by price position">
        {POSITION_ORDER.map((position) => {
          const value = byPosition.get(position) ?? 0;

          if (value === 0) {
            return null;
          }

          return (
            <div
              key={position}
              className="compbar__seg"
              style={{ background: POSITION_COLOR[position], flexGrow: value }}
              title={`${POSITION_LABEL[position]}: ${count(value)} (${((value / total) * 100).toFixed(1)}%)`}
            />
          );
        })}
      </div>

      <div className="compbar-legend">
        {POSITION_ORDER.map((position) => {
          const value = byPosition.get(position) ?? 0;

          return (
            <div className="compbar-legend__item" key={position}>
              <span className="compbar-legend__rule" style={{ background: POSITION_COLOR[position] }} />
              <span>
                <span className="compbar-legend__value">{count(value)}</span>
                <div className="compbar-legend__label">
                  {POSITION_LABEL[position]} · {((value / total) * 100).toFixed(0)}%
                </div>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
